/**
 * Contact – Contactpagina (Fase 2: identiteit verwerkingsverantwoordelijke).
 */
import { Link, useSearchParams } from 'react-router-dom';
import { AnimatedSection } from '../components/AnimatedSection';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';
import {
  LEGAL_BTW,
  LEGAL_CONTACT_EMAIL,
  LEGAL_ENTITY_NAME,
  LEGAL_KVK,
  LEGAL_PRODUCT_NAME,
  getLegalAddressLines,
  legalMailtoHref,
} from '../lib/legal-site-info';
import { useAuth } from '../lib/AuthContext';

export function Contact() {
  const { user, isAccountManager } = useAuth();
  const [searchParams] = useSearchParams();
  const onderwerp = searchParams.get('onderwerp');
  const isPasswordHelp = onderwerp === 'wachtwoord';
  const isEnterprise = onderwerp === 'enterprise';
  const addressLines = getLegalAddressLines();

  return (
    <div className="animate-page-enter">
      <SeoMeta
        path="/contact"
        title="Contact"
        description={`Neem contact op met ${LEGAL_PRODUCT_NAME} voor vragen over CalqTool™, demo, facturatie of support. Wij reageren op werkdagen — Nederlands bouw- en calculatieplatform.`}
      />
      <AnimatedSection delay={0.05}>
        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Contact' }]} />
        <h1 className="mb-6 text-2xl font-bold text-text">Contact</h1>
        <div className="max-w-2xl space-y-6 rounded-lg border border-border bg-surface-card p-6">
          {isPasswordHelp && (
            <div className="rounded-lg border border-sky/40 bg-sky/10 px-4 py-3 text-sm text-text">
              <p className="font-medium text-text">Wachtwoord vergeten</p>
              <p className="mt-2 text-text-muted">
                Automatisch wachtwoord herstellen via e-mail staat op onze roadmap. Tot die tijd: stuur een e-mail
                naar{' '}
                <a className="font-medium text-primary hover:underline" href={legalMailtoHref('Wachtwoord reset')}>
                  {LEGAL_CONTACT_EMAIL}
                </a>{' '}
                (noem het e-mailadres van je account), dan helpen we je verder.
              </p>
            </div>
          )}
          {isEnterprise && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-text">
              <p className="font-medium text-text">Enterprise / maatwerk</p>
              <p className="mt-2 text-text-muted">
                Voor grotere organisaties, SSO, SLA of maatwerklimieten: neem contact op via onderstaand adres. Vermeld
                ruimte voor een korte context (sector, aantal gebruikers, gewenste start).
              </p>
            </div>
          )}

          <p className="text-sm text-text-muted">
            Vragen over {LEGAL_PRODUCT_NAME}, privacy, facturatie of samenwerking? Hieronder vind je de gegevens van de
            verwerkingsverantwoordelijke en handige links.
          </p>

          <section className="rounded-lg border border-border bg-surface px-4 py-4">
            <h2 className="text-sm font-semibold text-text">Verwerkingsverantwoordelijke</h2>
            <p className="mt-2 text-sm text-text-muted">
              Het platform <strong className="text-text">{LEGAL_PRODUCT_NAME}</strong> wordt aangeboden door:
            </p>
            <p className="mt-2 text-sm font-medium text-text">{LEGAL_ENTITY_NAME}</p>
            <p className="mt-2 text-sm text-text-muted">
              {addressLines.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </p>
            <p className="mt-3 text-sm text-text-muted">
              E-mail:{' '}
              <a className="font-medium text-primary hover:underline" href={legalMailtoHref()}>
                {LEGAL_CONTACT_EMAIL}
              </a>
            </p>
            <p className="mt-1 text-sm text-text-muted">KvK: {LEGAL_KVK}</p>
            {LEGAL_BTW ? (
              <p className="mt-1 text-sm text-text-muted">BTW-id: {LEGAL_BTW}</p>
            ) : null}
          </section>

          <section className="rounded-lg border border-border/80 bg-surface px-4 py-4 text-sm text-text-muted">
            <h2 className="text-sm font-semibold text-text">Privacy &amp; AVG</h2>
            <p className="mt-2 leading-relaxed">
              Voor inzage, correctie, verwijdering, beperking, bezwaar, dataportabiliteit of vragen over verwerkers en
              doorgifte: zie het{' '}
              <Link to="/privacy" className="font-medium text-primary hover:underline">
                privacybeleid
              </Link>
              . Zakelijke klanten kunnen een{' '}
              <strong className="text-text">verwerkersovereenkomst (DPA)</strong> opvragen via{' '}
              <a className="font-medium text-primary hover:underline" href={legalMailtoHref('Verwerkersovereenkomst DPA')}>
                {LEGAL_CONTACT_EMAIL}
              </a>
              . Dit is informatief; juridische definitieve teksten volgen uit uw contract en (indien van toepassing)
              ondertekende verwerkersafspraken.
            </p>
          </section>

          <section className="space-y-3 text-sm text-text-muted">
            <p className="font-medium text-text">Hulp &amp; service</p>
            <p className="text-xs leading-relaxed text-text-muted">
              Eerst controleren of de dienst bereikbaar is en of uw vraag bij bekende oplossingen hoort; daarna kunt u de
              handleidingen openen voor stap-voor-stap uitleg (zelfde indeling als onder Account → Hulp &amp; contact en
              in de footer).
            </p>
            <p className="text-xs font-medium text-text">Service</p>
            <ul className="list-inside list-disc space-y-1">
              <li>
                <Link to="/status" className="text-primary hover:underline">
                  Dienststatus
                </Link>{' '}
                — is CalqTool online?
              </li>
              <li>
                <Link to="/troubleshoot" className="text-primary hover:underline">
                  Problemen oplossen
                </Link>{' '}
                — veelvoorkomende oorzaken (tekening, bibliotheek, inloggen)
              </li>
            </ul>
            <p className="text-xs font-medium text-text">Handleidingen</p>
            <p className="text-xs leading-relaxed text-text-muted">
              Twee verschillende &quot;bibliotheken&quot;: de{' '}
              <strong className="text-text">prijsbibliotheek</strong> (posten/catalogus op tab Posten) en de{' '}
              <strong className="text-text">documentenbibliotheek</strong> (tekeningen/PDF per dossier op tab Gegevens).
            </p>
            <ul className="list-inside list-disc space-y-1">
              <li>
                <Link to="/help/bibliotheek" className="text-primary hover:underline">
                  Prijsbibliotheek
                </Link>{' '}
                — catalogus, fair use
              </li>
              <li>
                <Link to="/help/tekening" className="text-primary hover:underline">
                  Tekening &amp; PDF
                </Link>{' '}
                — documentenbibliotheek, analyse, importeren in delen
              </li>
              <li>
                <Link to="/help/export" className="text-primary hover:underline">
                  Export &amp; offerte
                </Link>{' '}
                — rapportage, downloads, offerte
              </li>
            </ul>
          </section>

          <section className="space-y-2 text-sm text-text-muted">
            <p className="font-medium text-text">Juridisch &amp; product</p>
            <ul className="list-inside list-disc space-y-1">
              <li>
                <Link to="/privacy" className="text-primary hover:underline">
                  Privacybeleid
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-primary hover:underline">
                  Algemene voorwaarden
                </Link>
              </li>
              <li>
                <Link to="/cookies" className="text-primary hover:underline">
                  Cookies
                </Link>
              </li>
              <li>
                <Link to="/prijzen" className="text-primary hover:underline">
                  Pakketten en prijzen
                </Link>
              </li>
              {user?.authenticated && isAccountManager ? (
                <li>
                  <Link to="/account/billing" className="text-primary hover:underline">
                    Abonnement afrekenen (Facturatie)
                  </Link>
                </li>
              ) : null}
            </ul>
          </section>

          <Link to="/" className="inline-flex text-primary transition-colors duration-200 hover:underline">
            ← Terug naar home
          </Link>
        </div>
      </AnimatedSection>
    </div>
  );
}
